import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Warehouse, Globe, AlertCircle } from "lucide-react";
import { AUTH, LANG_TOGGLE } from "@/constants/testIds";
import { landingPath } from "@/constants/screens";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(landingPath(user), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("login.invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col items-center justify-center p-4">
      {/* Lang toggle */}
      <button data-testid={LANG_TOGGLE} onClick={toggle}
        className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg font-medium transition-all">
        <Globe className="w-4 h-4" />
        {lang.toUpperCase()}
      </button>

      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="mx-auto w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/50">
            <Warehouse className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>
            BoxPrep
          </h1>
          <p className="text-slate-400 text-sm">{t("login.subtitle")}</p>
        </div>

        {/* Login form */}
        <form data-testid={AUTH.loginForm} onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
          <h2 className="text-lg font-bold text-slate-900">{t("login.sign_in")}</h2>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-medium">{t("login.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="email@example.com"
              required
              autoComplete="email"
              data-testid={AUTH.emailInput}
              className="h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-sm font-medium">{t("login.password")}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              data-testid={AUTH.passwordInput}
              className="h-11"
            />
          </div>

          <Button type="submit" disabled={loading} data-testid={AUTH.submitBtn}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base active:scale-95 transition-transform">
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t("login.signing_in")}
              </span>
            ) : t("login.sign_in")}
          </Button>
        </form>
      </div>
    </div>
  );
}
